import React, { useEffect, type JSX } from "react";
import { RotateCcw } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { createAdminShellViewModel } from "./admin-shell-view-model";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminLocalizedNavLink } from "../../shared/locale/admin-dashboard-localized-link";
import { resolveAdminDashboardLocaleFromQuery } from "../../shared/locale/admin-dashboard-locale";
import { withAdminDashboardLocaleTo } from "../../shared/locale/admin-dashboard-locale-routing";
import {
  AdminManualRefreshProvider,
  useAdminManualRefresh,
} from "../../shared/query/admin-manual-refresh-context";

const shellCss = `
:root {
  color: #18212b;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.18), transparent 28%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.12), transparent 24%),
    linear-gradient(180deg, #f7f3ea 0%, #efe6d8 100%);
  font-family: "Segoe UI", sans-serif;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  min-height: 100vh;
}

a {
  color: inherit;
  text-decoration: none;
}

.admin-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
}

.admin-shell__sidebar {
  padding: 32px 24px;
  border-right: 1px solid rgba(24, 33, 43, 0.12);
  background: rgba(255, 250, 242, 0.76);
  backdrop-filter: blur(12px);
  display: grid;
  gap: 22px;
  align-content: start;
}

.admin-shell__eyebrow {
  margin: 0;
  color: #9a3412;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.admin-shell__brand {
  margin: 10px 0 0;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.admin-shell__nav-group {
  display: grid;
  gap: 10px;
}

.admin-shell__nav-title {
  margin: 0;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.admin-shell__nav {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}

.admin-shell__nav-link {
  display: block;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(24, 33, 43, 0.05);
  border: 1px solid transparent;
  font-size: 0.95rem;
  font-weight: 600;
  transition: 150ms ease;
}

.admin-shell__nav-link.active {
  background: rgba(154, 52, 18, 0.12);
  border-color: rgba(154, 52, 18, 0.24);
}

.admin-shell__main {
  padding: 32px;
  display: grid;
  gap: 24px;
}

.admin-shell__topbar {
  display: flex;
  justify-content: flex-end;
}

.admin-shell__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.admin-shell__locale-box {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(24, 33, 43, 0.1);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.admin-shell__locale-label {
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
}

.admin-shell__locale-actions {
  display: inline-flex;
  gap: 8px;
}

.admin-shell__locale-button {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #fff;
  color: #18212b;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
}

.admin-shell__locale-button.is-active {
  border-color: rgba(15, 118, 110, 0.3);
  background: rgba(15, 118, 110, 0.12);
  color: #0f766e;
}

.admin-shell__refresh-button {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border: 1px solid rgba(15, 118, 110, 0.3);
  border-radius: 999px;
  background: rgba(15, 118, 110, 0.12);
  color: #0f766e;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
}

.admin-shell__refresh-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.admin-shell__slot {
  min-width: 0;
}

@media (max-width: 960px) {
  .admin-shell {
    grid-template-columns: 1fr;
  }

  .admin-shell__sidebar {
    border-right: 0;
    border-bottom: 1px solid rgba(24, 33, 43, 0.12);
  }

  .admin-shell__nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .admin-shell__main {
    padding: 24px;
  }
}

@media (max-width: 600px) {
  .admin-shell__sidebar {
    padding: 16px;
    gap: 16px;
  }

  .admin-shell__brand {
    font-size: 1.2rem;
  }

  .admin-shell__nav {
    grid-template-columns: 1fr;
  }

  .admin-shell__nav-link {
    padding: 10px 12px;
  }

  .admin-shell__main {
    padding: 16px;
    gap: 16px;
  }

  .admin-shell__topbar,
  .admin-shell__actions {
    width: 100%;
  }

  .admin-shell__actions {
    justify-content: space-between;
  }
}
`;

export function AdminShell(): JSX.Element {
  return (
    <AdminManualRefreshProvider>
      <AdminShellContent />
    </AdminManualRefreshProvider>
  );
}

function AdminShellContent(): JSX.Element {
  const { locale, setLocale } = useAdminDashboardLocale();
  const { disabled: isManualRefreshDisabled, refresh } = useAdminManualRefresh();
  const location = useLocation();
  const navigate = useNavigate();
  const viewModel = createAdminShellViewModel(locale);

  useEffect(() => {
    const queryLocale = resolveAdminDashboardLocaleFromQuery(location.search);
    if (queryLocale && queryLocale !== locale) {
      setLocale(queryLocale);
    }
  }, [locale, location.search, setLocale]);

  useEffect(() => {
    const queryLocale = resolveAdminDashboardLocaleFromQuery(location.search);
    if (queryLocale) {
      return;
    }

    navigate(
      withAdminDashboardLocaleTo(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        locale,
      ),
      { replace: true },
    );
  }, [locale, location.hash, location.pathname, location.search, navigate]);

  function handleLocaleChange(nextLocale: typeof locale): void {
    setLocale(nextLocale);
    navigate(
      withAdminDashboardLocaleTo(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        nextLocale,
      ),
      { replace: true },
    );
  }

  return (
    <>
      <style>{shellCss}</style>
      <div className="admin-shell">
        <aside className="admin-shell__sidebar">
          <div>
            <p className="admin-shell__eyebrow">{viewModel.shellEyebrow}</p>
            <h1 className="admin-shell__brand">{viewModel.shellBrand}</h1>
          </div>

          <nav aria-label="Admin navigation" className="admin-shell__nav-group">
            {viewModel.navigationSections.map((section) => (
              <div className="admin-shell__nav-group" key={section.label}>
                <p className="admin-shell__nav-title">{section.label}</p>
                <ul className="admin-shell__nav">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <AdminLocalizedNavLink
                        className="admin-shell__nav-link"
                        to={item.to}
                        end={item.to === "/admin"}
                      >
                        {item.label}
                      </AdminLocalizedNavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        <main className="admin-shell__main">
          <header className="admin-shell__topbar">
            <div className="admin-shell__actions">
              <button
                className="admin-shell__refresh-button"
                disabled={isManualRefreshDisabled}
                onClick={refresh}
                type="button"
              >
                <RotateCcw size={16} />
                {locale === "ko" ? "새로고침" : "Refresh"}
              </button>
              <div className="admin-shell__locale-box">
                <span className="admin-shell__locale-label">{viewModel.localeLabel}</span>
                <div className="admin-shell__locale-actions">
                  {viewModel.localeOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      className={`admin-shell__locale-button${option.key === locale ? " is-active" : ""}`}
                      onClick={() => handleLocaleChange(option.key)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </header>

          <div className="admin-shell__slot">
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}
